import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { useSelector, useDispatch } from "react-redux";
import { fetchRepairsAsync } from "../../../../../../store/Slice/repairSlice";
import type { RootState, AppDispatch } from "../../../../../../store/store";

interface RepairBarGraphProps {
  type?: 'line' | 'bar';
  className?: string;
}

export const RepairBarGraph: React.FC<RepairBarGraphProps> = ({ type = 'bar', className }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { items } = useSelector((state: RootState) => state.repairs);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(fetchRepairsAsync({ page: 1, limit: 1000 }) as any);
      setLoading(false);
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (!chartRef.current || loading) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const sortedMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const year = new Date().getFullYear();

    const monthlyData = items.reduce((acc, item) => {
      const date = new Date(item.receivingDate || '');
      if (isNaN(date.getTime()) || date.getFullYear() !== year) return acc;
      
      const month = date.toLocaleString('default', { month: 'short' });
      if (!acc[month]) {
        acc[month] = { Repair: 0, Replacement: 0, Pending: 0, Delayed: 0, Complete: 0 };
      }

      if (item.requestType === "repair") {
        acc[month].Repair += 1;
      } else if (item.requestType === "replacement") {
        acc[month].Replacement += 1;
      }

      const today = new Date();
      if (!item.expectedDeliveryDate) {
        acc[month].Pending += 1;
      } else if (new Date(item.expectedDeliveryDate) >= today) {
        acc[month].Pending += 1;
      } else {
        acc[month].Delayed += 1;
      }

      // ⭐ NEW — APPROVED (COMPLETE)
      if (item.isApproved) {
        acc[month].Complete += 1;
      }

      return acc;
    }, {} as { [key: string]: { [key: string]: number } });

    const labels = sortedMonths.filter(month => monthlyData[month]);
    const repairData = labels.map(month => monthlyData[month]?.Repair || 0);
    const replacementData = labels.map(month => monthlyData[month]?.Replacement || 0);
    const pendingData = labels.map(month => monthlyData[month]?.Pending || 0);
    const delayedData = labels.map(month => monthlyData[month]?.Delayed || 0);
    const completeData = labels.map(month => monthlyData[month]?.Complete || 0);

    const data = {
      labels,
      datasets: [
        {
          label: 'Repair',
          data: repairData,
          backgroundColor: 'hsl(217 91% 60%)',
          borderColor: 'hsl(217 91% 60%)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
          fill: type === 'line'
        },
        {
          label: 'Replacement',
          data: replacementData,
          backgroundColor: 'hsl(262 83% 58%)',
          borderColor: 'hsl(262 83% 58%)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
          fill: type === 'line'
        },
        {
          label: 'Pending',
          data: pendingData,
          backgroundColor: 'hsl(43 96% 56%)',
          borderColor: 'hsl(43 96% 56%)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
          fill: type === 'line'
        },
        {
          label: 'Delayed',
          data: delayedData,
          backgroundColor: 'hsl(33 100% 50%)',
          borderColor: 'hsl(33 100% 50%)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
          fill: type === 'line'
        },
        // ⭐ NEW — COMPLETE (APPROVED)
        {
          label: 'Complete',
          data: completeData,
          backgroundColor: 'hsl(142 76% 36%)',
          borderColor: 'hsl(142 76% 36%)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
          fill: type === 'line'
        }
      ]
    };

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const config = {
      type,
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom' as const,
            labels: {
              font: { size: 12 },
              padding: 15,
              usePointStyle: true
            }
          },
          tooltip: {
            backgroundColor: 'hsl(var(--popover))',
            titleColor: 'hsl(var(--popover-foreground))',
            bodyColor: 'hsl(var(--popover-foreground))',
            borderColor: 'hsl(var(--border))',
            borderWidth: 1
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              drawBorder: false,
              color: 'hsl(var(--border))'
            },
            ticks: {
              color: 'hsl(var(--muted-foreground))'
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: 'hsl(var(--muted-foreground))'
            }
          }
        }
      }
    };

    chartInstance.current = new Chart(ctx, config);

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [items, type, loading]);

  return (
    <div className={className}>
      {loading ? (
        <div className="flex items-center justify-center w-full h-full">
          <p className="text-gray-500">Loading chart data...</p>
        </div>
      ) : (
        <canvas ref={chartRef} />
      )}
    </div>
  );
};

export default RepairBarGraph;
